import React, { useState, useEffect, useCallback } from 'react';
import {
  CompanyCRMViewProps,
  GetCompanyDetailsResponse,
  CreateCrmNoteResponse,
  CrmNote,
  CrmEvents,
  CrmNoteCreatedEvent,
  CrmOrganisationUpdatedEvent,
} from '@dreamai/types';

interface CustomField {
  id: string;
  fieldName: string;
  fieldType: 'boolean' | 'string' | 'integer' | 'enum' | 'date';
  enumValues: string[] | null;
  value: string | null;
}
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../../api';
import { usePusher } from '../../../../contexts/PusherContext';

import { ReactComponent as GlobeIcon } from '../../../../icons/globe.svg';
import { ReactComponent as LinkedInIcon } from '../../../../icons/linkedin.svg';
import { ReactComponent as InstagramIcon } from '../../../../icons/instagram.svg';
import { ReactComponent as NotesIcon } from '../../../../icons/notes.svg';
import { ReactComponent as PhoneIcon } from '../../../../icons/phone.svg';
import { ReactComponent as EmailIcon } from '../../../../icons/email.svg';
import { ReactComponent as TrashIcon } from '../../../../icons/trash-outline.svg';
import { ReactComponent as EditIcon } from '../../../../icons/edit.svg';
import OrgAvatar from '../../../common/OrgAvatar';
import ErrorState from '../../../common/ErrorState';

import './company-crm-view.scss';

interface CompanyCRMViewComponentProps extends CompanyCRMViewProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

const CompanyCRMView: React.FC<CompanyCRMViewComponentProps> = ({
  companyId,
  threadId,
  updateThreadViewVisualDescription,
}) => {
  const [companyData, setCompanyData] = useState<
    GetCompanyDetailsResponse['company'] | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [editingFieldValue, setEditingFieldValue] = useState<string>('');
  const { getAccessTokenSilently } = useAuth0();
  const pusherChannel = usePusher();

  const handleSaveNote = async () => {
    if (!noteText.trim() || !companyData) return;

    // Create optimistic note with temporary ID
    const tempId = -Date.now();
    const optimisticNote: CrmNote = {
      id: tempId,
      knownOrgId: companyData.id,
      prospectId: null,
      userId: '',
      agentId: null,
      userName: 'You',
      content: noteText.trim(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Add optimistically
    setCompanyData({
      ...companyData,
      notes: [optimisticNote, ...companyData.notes],
    });
    const savedNoteText = noteText.trim();
    setNoteText('');
    setIsAddingNote(false);
    setNoteError(null);

    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.post<CreateCrmNoteResponse>(
        '/crm-data/notes',
        {
          knownOrgId: companyData.id,
          content: savedNoteText,
        },
      );

      if (response.data.success && response.data.note) {
        // Replace optimistic note with real note
        setCompanyData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            notes: prev.notes.map((n) =>
              n.id === tempId ? response.data.note! : n,
            ),
          };
        });
      } else {
        // Remove optimistic note on failure
        setCompanyData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            notes: prev.notes.filter((n) => n.id !== tempId),
          };
        });
        setNoteError('Failed to save note. Please try again.');
      }
    } catch (err) {
      console.error('Error saving note:', err);
      // Remove optimistic note on error
      setCompanyData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          notes: prev.notes.filter((n) => n.id !== tempId),
        };
      });
      setNoteError('Failed to save note. Please try again.');
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!companyData) return;

    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.delete(`/crm-data/notes/${noteId}`);

      if (response.data.success) {
        setCompanyData({
          ...companyData,
          notes: companyData.notes.filter((n: CrmNote) => n.id !== noteId),
        });
      }
    } catch (err) {
      console.error('Error deleting note:', err);
    }
  };

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = await getAccessTokenSilently();
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get<GetCompanyDetailsResponse>(
          `/crm-data/companies/${companyId}`,
        );

        if (response.data.success) {
          setCompanyData(response.data.company);
          // Initialize custom fields from company data
          if (response.data.company.customFields) {
            setCustomFields(
              response.data.company.customFields as CustomField[],
            );
          }

          if (updateThreadViewVisualDescription) {
            await updateThreadViewVisualDescription(
              `Viewing company: ${response.data.company.name}`,
            );
          }
        } else {
          setError(response.data.error || 'Failed to load company');
        }
      } catch (err: any) {
        console.error('Error fetching company:', err);
        setError(
          err.response?.status === 404
            ? 'Company not found'
            : 'Failed to load company',
        );
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      fetchCompanyData();
    } else {
      setError('No company ID provided');
      setLoading(false);
    }
  }, [companyId, getAccessTokenSilently, updateThreadViewVisualDescription]);

  const handleSaveCustomField = async (fieldId: string, value: string) => {
    if (!companyData) return;

    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.put<{
        success: boolean;
        field: { id: string; fieldName: string; value: string | null };
      }>(`/crm-data/companies/${companyData.id}/custom-fields/${fieldId}`, {
        value: value || null,
      });

      if (response.data.success) {
        setCustomFields((prev) =>
          prev.map((f) =>
            f.id === fieldId ? { ...f, value: response.data.field.value } : f,
          ),
        );
      }
    } catch (err) {
      console.error('Error saving custom field:', err);
    } finally {
      setEditingFieldId(null);
      setEditingFieldValue('');
    }
  };

  const handleCancelEdit = () => {
    setEditingFieldId(null);
    setEditingFieldValue('');
  };

  const renderCustomFieldInput = (field: CustomField) => {
    if (field.fieldType === 'boolean') {
      return (
        <select
          className='custom-field-select'
          value={editingFieldValue}
          onChange={(e) => setEditingFieldValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') handleCancelEdit();
          }}
          autoFocus
        >
          <option value=''>Not set</option>
          <option value='true'>Yes</option>
          <option value='false'>No</option>
        </select>
      );
    }

    if (field.fieldType === 'enum' && field.enumValues) {
      return (
        <select
          className='custom-field-select'
          value={editingFieldValue}
          onChange={(e) => setEditingFieldValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') handleCancelEdit();
          }}
          autoFocus
        >
          <option value=''>Not set</option>
          {field.enumValues.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }

    if (field.fieldType === 'date') {
      return (
        <input
          type='date'
          className='custom-field-input'
          value={editingFieldValue}
          onChange={(e) => setEditingFieldValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSaveCustomField(field.id, editingFieldValue);
            } else if (e.key === 'Escape') {
              handleCancelEdit();
            }
          }}
          autoFocus
        />
      );
    }

    return (
      <input
        type={field.fieldType === 'integer' ? 'number' : 'text'}
        className='custom-field-input'
        value={editingFieldValue}
        onChange={(e) => setEditingFieldValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleSaveCustomField(field.id, editingFieldValue);
          } else if (e.key === 'Escape') {
            handleCancelEdit();
          }
        }}
        autoFocus
      />
    );
  };

  const renderCustomFieldValue = (field: CustomField) => {
    const isEditing = editingFieldId === field.id;

    if (isEditing) {
      return (
        <div className='custom-field-edit-container'>
          {renderCustomFieldInput(field)}
          <div className='custom-field-actions'>
            <button
              className='custom-field-save-btn'
              onClick={() => handleSaveCustomField(field.id, editingFieldValue)}
            >
              Save
            </button>
            <button
              className='custom-field-cancel-btn'
              onClick={handleCancelEdit}
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    // Display value
    const displayValue = (() => {
      if (field.value === null || field.value === '') return 'Not set';
      if (field.fieldType === 'boolean') {
        return field.value === 'true' ? 'Yes' : 'No';
      }
      if (field.fieldType === 'date') {
        return new Date(field.value).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
      }
      return field.value;
    })();

    return (
      <button
        type='button'
        className={`custom-field-value ${field.value === null || field.value === '' ? 'not-set' : ''}`}
        onClick={() => {
          setEditingFieldId(field.id);
          setEditingFieldValue(field.value || '');
        }}
      >
        <span>{displayValue}</span>
        <EditIcon className='custom-field-edit-icon' />
      </button>
    );
  };

  // Refetch full company data when organisation is updated via pusher
  const refetchCompanyData = useCallback(async () => {
    if (!companyId) return;

    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get<GetCompanyDetailsResponse>(
        `/crm-data/companies/${companyId}`,
      );

      if (response.data.success) {
        setCompanyData(response.data.company);
      }
    } catch (err) {
      console.error('Error refetching company data:', err);
    }
  }, [companyId, getAccessTokenSilently]);

  // Refetch notes when a CRM note is created via pusher
  const refetchNotes = useCallback(async () => {
    if (!companyId) return;

    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get<GetCompanyDetailsResponse>(
        `/crm-data/companies/${companyId}`,
      );

      if (response.data.success) {
        setCompanyData((prev) => {
          if (!prev) return response.data.company;
          return {
            ...prev,
            notes: response.data.company.notes,
          };
        });
      }
    } catch (err) {
      console.error('Error refetching notes:', err);
    }
  }, [companyId, getAccessTokenSilently]);

  // Listen for CRM organisation updated events
  useEffect(() => {
    if (!pusherChannel || !companyData) return;

    const handleOrganisationUpdated = (data: CrmOrganisationUpdatedEvent) => {
      // Only refetch if this is the company being viewed
      if (data.organisationId === companyData.id) {
        refetchCompanyData();
      }
    };

    pusherChannel.bind(
      CrmEvents.ORGANISATION_UPDATED,
      handleOrganisationUpdated,
    );

    return () => {
      pusherChannel.unbind(
        CrmEvents.ORGANISATION_UPDATED,
        handleOrganisationUpdated,
      );
    };
  }, [pusherChannel, companyData, refetchCompanyData]);

  // Listen for CRM note created events
  useEffect(() => {
    if (!pusherChannel || !companyData) return;

    const handleNoteCreated = (data: CrmNoteCreatedEvent) => {
      // Only refetch if the note is for this company
      if (data.knownOrgId === companyData.id) {
        refetchNotes();
      }
    };

    pusherChannel.bind(CrmEvents.NOTE_CREATED, handleNoteCreated);

    return () => {
      pusherChannel.unbind(CrmEvents.NOTE_CREATED, handleNoteCreated);
    };
  }, [pusherChannel, companyData, refetchNotes]);

  if (loading) {
    return (
      <div className='company-crm-view loading'>
        <div className='spinner-border spinner-border-sm me-2' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
        <span>Loading company...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className='company-crm-view error'>
        <div className='alert alert-danger' role='alert'>
          {error}
        </div>
      </div>
    );
  }

  if (!companyData) return null;

  // Calculate number of info rows for grid template
  let infoRowCount = 0;
  if (companyData.domain) infoRowCount++;
  if (companyData.linkedinUrl) infoRowCount++;
  if (companyData.instagramHandle) infoRowCount++;
  if (companyData.phone) infoRowCount++;

  return (
    <div className='company-crm-view'>
      <div className='company-header'>
        <OrgAvatar
          imageUrl={companyData.logoUrl || undefined}
          orgName={companyData.name}
          size={62}
        />
        <div className='company-header-info'>
          <h2 className='company-name'>{companyData.name}</h2>
          <span
            className={`customer-status-badge status-${companyData.customerStatus || 'opportunity'}`}
          >
            {(() => {
              const statusLabels: Record<string, string> = {
                none: 'None',
                opportunity: 'Opportunity',
                trial: 'Trial',
                current: 'Current Customer',
                past: 'Past Customer',
                churned: 'Churned',
              };
              return (
                statusLabels[companyData.customerStatus || 'opportunity'] ||
                'Opportunity'
              );
            })()}
          </span>
        </div>
      </div>

      <div className='company-body'>
        <div className='section about-section'>
          <h3 className='section-title'>About</h3>
          <p className='about-text'>
            {companyData.description ||
              'No description available for this company yet.'}
          </p>
        </div>

        {companyData.funnels.length > 0 && (
          <div className='section funnels-section'>
            <h3 className='section-title'>Funnels</h3>
            <div className='funnel-tags'>
              {companyData.funnels.map((funnel) => (
                <span key={funnel.id} className='funnel-tag'>
                  {funnel.name}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className='section notes-section'>
          <div className='section-header'>
            <h3 className='section-title'>Notes</h3>
            {!isAddingNote && (
              <button
                className='add-note-btn'
                onClick={() => {
                  setIsAddingNote(true);
                  setNoteError(null);
                }}
              >
                + Add note
              </button>
            )}
          </div>
          {noteError && (
            <ErrorState title='Error saving note' subtitle={noteError} />
          )}
          {isAddingNote ? (
            <div className='note-editor'>
              <textarea
                className='note-textarea'
                placeholder='Write your note here...'
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
              <div className='note-actions'>
                <button
                  className='cancel-btn'
                  onClick={() => {
                    setIsAddingNote(false);
                    setNoteText('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className='save-btn'
                  onClick={handleSaveNote}
                  disabled={!noteText.trim()}
                >
                  Save note
                </button>
              </div>
            </div>
          ) : companyData.notes && companyData.notes.length > 0 ? (
            <div className='notes-list'>
              {companyData.notes.map((note: CrmNote) => (
                <div key={note.id} className='note-item'>
                  <div className='note-header'>
                    <span className='note-author'>
                      {new Date(note.createdAt).toLocaleString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit',
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}{' '}
                      - By {note.userName || 'Unknown'}
                    </span>

                    <button
                      className='note-delete-btn'
                      onClick={() => handleDeleteNote(note.id)}
                      title='Delete note'
                    >
                      <TrashIcon />
                    </button>
                  </div>
                  <p className='note-content'>{note.content}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className='notes-empty'>
              <NotesIcon className='notes-icon' />
              <p>You've not added any notes yet.</p>
            </div>
          )}
        </div>

        <div className='section company-info-section'>
          <h3 className='section-title'>Company Information</h3>

          <div
            className='info-rows'
            style={{
              gridTemplateRows: `repeat(${infoRowCount}, minmax(0, 1fr))`,
            }}
          >
            {companyData.domain && (
              <div className='info-row'>
                <GlobeIcon className='info-icon' />
                <div className='info-content'>
                  <span className='info-label'>Website</span>
                  <a
                    href={`https://${companyData.domain}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='info-value link'
                  >
                    {companyData.domain}
                  </a>
                </div>
              </div>
            )}

            {companyData.linkedinUrl && (
              <div className='info-row'>
                <LinkedInIcon className='info-icon' />
                <div className='info-content'>
                  <span className='info-label'>LinkedIn</span>
                  <a
                    href={companyData.linkedinUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='info-value link'
                  >
                    {companyData.linkedinUrl}
                  </a>
                </div>
              </div>
            )}

            {companyData.instagramHandle && (
              <div className='info-row'>
                <InstagramIcon className='info-icon' />
                <div className='info-content'>
                  <span className='info-label'>Instagram</span>
                  <a
                    href={`https://instagram.com/${companyData.instagramHandle.replace('@', '')}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='info-value link'
                  >
                    {companyData.instagramHandle}
                  </a>
                </div>
              </div>
            )}

            {companyData.phone && (
              <div className='info-row'>
                <PhoneIcon className='info-icon' />
                <div className='info-content'>
                  <span className='info-label'>Phone</span>
                  <span className='info-value'>{companyData.phone}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {customFields.length > 0 && (
          <div className='section custom-fields-section'>
            <h3 className='section-title'>Custom Fields</h3>
            <div className='custom-fields-list'>
              {customFields.map((field) => (
                <div key={field.id} className='custom-field-row'>
                  <span className='custom-field-label'>{field.fieldName}</span>
                  {renderCustomFieldValue(field)}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className='section contacts-section'>
          <h3 className='section-title'>
            Known Contacts ({companyData.prospects.length})
          </h3>
          {companyData.prospects.length > 0 ? (
            <div className='contacts-list'>
              {companyData.prospects.map((prospect) => (
                <div key={prospect.id} className='contact-row'>
                  <OrgAvatar orgName={prospect.name} size={40} />
                  <div className='contact-info'>
                    <span className='contact-name'>{prospect.name}</span>
                    <span className='contact-title'>{prospect.jobTitle}</span>
                    {prospect.email && (
                      <span className='contact-email'>
                        <EmailIcon className='contact-email-icon' />
                        {prospect.email}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className='contacts-empty'>
              No contacts found for this company.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyCRMView;
